import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Plus, Edit, Trash2, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TranslatableInput } from "./TranslatableInput";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedContent } from "@/types";

interface Category {
  id: string;
  name: LocalizedContent;
  description: LocalizedContent;
  is_active: boolean;
  created_at: string;
}

export const CategoriesManager = () => {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<{
    name: LocalizedContent;
    description: LocalizedContent;
  }>({
    name: "",
    description: ""
  });
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchCategories();
  }, []);

  const logAdminAction = async (action: string, entityId?: string, details?: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('admin_audit_log').insert({
        admin_user_id: user?.id,
        admin_email: user?.email,
        action,
        entity_type: 'service_category',
        entity_id: entityId,
        details
      });
    } catch (error) {
      console.error('Failed to log admin action:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: t('admin.categories.errorFetchTitle', 'Error'),
        description: t('admin.categories.errorFetch', 'Failed to fetch categories'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingCategory) {
        const { error } = await supabase
          .from('service_categories')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .update({
            name: formData.name,
            description: formData.description
          } as any)
          .eq('id', editingCategory.id);

        if (error) throw error;

        await logAdminAction('CATEGORY_UPDATED', editingCategory.id, {
          old_data: editingCategory,
          new_data: formData
        });

        toast({
          title: t('common.success', 'Success'),
          description: t('admin.categories.updated', 'Category updated successfully'),
        });
      } else {
        const { data, error } = await supabase
          .from('service_categories')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert({
            name: formData.name,
            description: formData.description
          } as any)
          .select()
          .single();

        if (error) throw error;

        await logAdminAction('CATEGORY_CREATED', data.id, formData);

        toast({
          title: t('common.success', 'Success'),
          description: t('admin.categories.created', 'Category created successfully'),
        });
      }

      setFormData({ name: "", description: "" });
      setEditingCategory(null);
      setIsDialogOpen(false);
      fetchCategories();
    } catch (error: any) {
      toast({
        title: t('common.error', 'Error'),
        description: error.message || t('admin.categories.errorSave', 'Failed to save category'),
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (category: Category) => {
    try {
      const { error } = await supabase
        .from('service_categories')
        .update({ is_active: !category.is_active })
        .eq('id', category.id);

      if (error) throw error;

      await logAdminAction('CATEGORY_STATUS_CHANGED', category.id, {
        from_active: category.is_active,
        to_active: !category.is_active
      });

      toast({
        title: t('common.success', 'Success'),
        description: !category.is_active
          ? t('admin.categories.activated', 'Category activated')
          : t('admin.categories.deactivated', 'Category deactivated'),
      });

      fetchCategories();
    } catch (error: any) {
      toast({
        title: t('common.error', 'Error'),
        description: error.message || t('admin.categories.errorUpdate', 'Failed to update category'),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (category: Category) => {
    if (!confirm(t('admin.categories.confirmDelete', `Are you sure you want to delete this category?`))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('service_categories')
        .delete()
        .eq('id', category.id);

      if (error) throw error;

      await logAdminAction('CATEGORY_DELETED', category.id, {
        deleted_category: category
      });

      toast({
        title: t('common.success', 'Success'),
        description: t('admin.categories.deleted', 'Category deleted successfully'),
      });

      fetchCategories();
    } catch (error: any) {
      toast({
        title: t('common.error', 'Error'),
        description: error.message || t('admin.categories.errorDelete', 'Failed to delete category'),
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingCategory(null);
    setFormData({ name: "", description: "" });
    setIsDialogOpen(true);
  };

  if (loading) {
    return <div className="flex justify-center py-8">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{t('admin.categories.title', 'Service Categories')}</h2>
          <p className="text-muted-foreground">{t('admin.categories.subtitle', 'Manage photography service categories')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t('admin.categories.addButton', 'Add Category')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCategory
                  ? t('admin.categories.editTitle', 'Edit Category')
                  : t('admin.categories.createTitle', 'Create New Category')}
              </DialogTitle>
              <DialogDescription>
                {editingCategory
                  ? t('admin.categories.editDescription', 'Update the category details below')
                  : t('admin.categories.createDescription', 'Add a new service category for your photography services')
                }
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <TranslatableInput
                  label={t('admin.categories.nameLabel', 'Category Name')}
                  value={formData.name}
                  onChange={(val) => setFormData({ ...formData, name: val })}
                  placeholder={t('admin.categories.namePlaceholder', 'e.g., Wedding Photography')}
                />
              </div>

              <div className="space-y-2">
                <TranslatableInput
                  label={t('admin.categories.descriptionLabel', 'Description')}
                  component="textarea"
                  value={formData.description}
                  onChange={(val) => setFormData({ ...formData, description: val })}
                  placeholder={t('admin.categories.descriptionPlaceholder', 'Brief description of this category')}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit">
                  {editingCategory
                    ? t('admin.categories.updateButton', 'Update Category')
                    : t('admin.categories.createButton', 'Create Category')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => (
          <Card key={category.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  {getLocalized(category.name)}
                </CardTitle>
                <Badge variant={category.is_active ? "default" : "secondary"}>
                  {category.is_active
                    ? t('admin.categories.active', 'Active')
                    : t('admin.categories.inactive', 'Inactive')}
                </Badge>
              </div>
              {category.description && (
                <CardDescription>{getLocalized(category.description)}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={category.is_active}
                    onCheckedChange={() => handleToggleActive(category)}
                  />
                  <span className="text-sm">{t('admin.categories.activeToggle', 'Active')}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(category)}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  {hasPermission('delete_categories') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(category)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {categories.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">{t('admin.categories.empty', 'No categories found. Create your first category!')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};